import React, { useRef } from "react";
import { RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCServicesApi } from "react-common-components";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import { useTranslation } from "src/context/Translations/context";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { standardErrorMessages } from "src/utils/errors";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import CheckPermission from "src/components/CheckPermission";
import ListRisks from "./ListRisks";
import { RisksSituation, IListOfRisks, IRefIRisksModals, IRefModalAnalysis, IRefModalHistoric, IRefmodalRegisterIncident, IRisks } from "../interface";
import ModalsRisk from "../modals";
import CardRisks from "./CardRisks";
import ModalRiskAnalysis from "./modalAnalysis";
import ModalRegisterIncident from "./modalRegisterIncident";
import ModalHistoric from "./modalHistoric";

const ListOfRisks: React.FC<IListOfRisks> = ({
	layoutList,
	layoutListRisks,
	data,
	filterListSituation,
	filterListQuadrant,
	onCallbackDelete,
	onCallbackUpdate
}) => {
	const {translate} = useTranslation();
	const {confirm} = useConfirmModal();
	const {addToast} = RCCHookToast();
	const { administrator, superUser, user, checkIfHasSpecificPermission } = useAuth();

	const refModals = useRef<IRefIRisksModals>(null);
	const refModalAnalysis = useRef<IRefModalAnalysis>(null);
	const refModalHistoric = useRef<IRefModalHistoric>(null);
	const refRegisterOccurence = useRef<IRefmodalRegisterIncident>(null);

	const reopenRisk = (rib001Id: number, rib001Titulo: string) => {
		confirm(
			translate("Reabrir risco"), 
			`${translate("Deseja realmente reabrir o risco")}: ${rib001Titulo}?`,
			async () => {
				await RCCServicesApi.putRequest({
					method: "Risks/Reopen",
					urlParameters: {
						id: rib001Id
					}
				});

				if(onCallbackUpdate)
					onCallbackUpdate(rib001Id);

				addToast({
					color: "green",
					description: translate("Risco reaberto com sucesso"),
					title: translate("Sucesso")
				});
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao reabir risco"), addToast);
			},
			false
		);
	};

	const options = (row: IRisks) => {
		
		const isAdminOrSuperOrAutority = administrator || superUser || row.rib001Efa005Id === user.efa005Id;
		const canCreateUpdate = checkIfHasSpecificPermission("/risks/panel", PermissionEnum.CREATE_UPDATE) && row.userPermission === 2;

		return (
			<>
				<RCCGrid 
					container
					item
					styleInline="justify-content: flex-end"
				>
					<OptionsColumn>
						{
							isAdminOrSuperOrAutority || canCreateUpdate ?
								<>
									<RCCDropDownItem
										selfEnclose
										onClick={() => { refModals.current?.actionButtonChange(row, !!(row.rib001Situacao === RisksSituation.FINALY || row.rib001Situacao === RisksSituation.CANCELED));}}
									>
										<RCCIcon name={row.rib001Situacao === RisksSituation.FINALY || row.rib001Situacao === RisksSituation.CANCELED ? "eye" : "pencil"}/>
				  								{row.rib001Situacao === RisksSituation.FINALY || row.rib001Situacao === RisksSituation.CANCELED ? translate("Visualizar") : translate("Alterar")}
									</RCCDropDownItem>

									{(row.rib001Situacao !== RisksSituation.FINALY && row.rib001Situacao !== RisksSituation.CANCELED) && (
										<>
						
											<RCCDropDownItem
												selfEnclose
												onClick={() => { refModalAnalysis.current?.actionButtonAdd(row); }}
											>
												<RCCIcon name="plus" 
												/>
												{translate("Adicionar análise")}
											</RCCDropDownItem>
											<RCCDropDownItem 
												selfEnclose
												onClick={() => { refRegisterOccurence.current?.actionButtonRegisterIncident(row); }}
											>
												<RCCIcon name="checkCircle" 
												/>
												{translate("Registro de incidência")}
											</RCCDropDownItem>
										</>
									)}

									{(row.rib001Situacao === RisksSituation.FINALY || row.rib001Situacao === RisksSituation.CANCELED) && (
										<RCCDropDownItem
											selfEnclose
											onClick={() => reopenRisk(row.rib001Id, row.rib001Titulo)}
										>
											<RCCIcon name="arrowBack" />
											{translate("Reabrir")}
										</RCCDropDownItem>
									)}
									<RCCDropDownItem 
										selfEnclose
										onClick={() => { refModalHistoric.current?.actionButtonHistoric(row); }}
									>
										<RCCIcon name="history" 
										/>
										{translate("Histórico")}
									</RCCDropDownItem>

									{(isAdminOrSuperOrAutority || row.userPermission === 2) && (
										<CheckPermission path="/risks/panel" minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
											<RCCDropDownItem 
												selfEnclose
												onClick={() => { refModals.current?.actionButtonDelete(row); }}
											>
												<RCCIcon name="trash" 
												/>
												{translate("Excluir")}
											</RCCDropDownItem>
										</CheckPermission>
									)}
								</>
								:
								<>
									<RCCDropDownItem 
										selfEnclose
										onClick={() => { refModalHistoric.current?.actionButtonHistoric(row); }}
									>
										<RCCIcon name="history" 
										/>
										{translate("Histórico")}
									</RCCDropDownItem>
								</>
						}
					</OptionsColumn>
				</RCCGrid>
				
			</>
			
		);};
	
	const FilterSituationAndQuadrant = (risk: any) => risk.rib001Situacao === filterListSituation && risk.rib001Quadrante === filterListQuadrant;
	const FilerSituation = (risk: any) => risk.rib001Situacao === filterListSituation;
	const FilterQuadrant = (risk: any) => risk.rib001Quadrante === filterListQuadrant;

	const FilterOption = () => {
		if(filterListSituation && filterListQuadrant)
			return FilterSituationAndQuadrant;

		if(filterListSituation)
			return FilerSituation;

		if(filterListQuadrant)
			return FilterQuadrant;

		return undefined;
	};
	return (
		<>
		
			{layoutList === "card" ? (
				<CardRisks 
					data={data}
					options={options}
					filter={FilterOption()} 
				/>
			): (
				<ListRisks 
					data={data}
					options={options}
					layout={layoutListRisks}
					filter={FilterOption()} 
				/>
			)}

			<ModalsRisk ref={refModals} onCallbackDelete={onCallbackDelete} onCallbackUpdate={onCallbackUpdate} />
			<ModalRiskAnalysis ref={refModalAnalysis} onCallbackUpdate={onCallbackUpdate}/>
			<ModalHistoric ref={refModalHistoric} onCallback={onCallbackUpdate}/>
			<ModalRegisterIncident ref={refRegisterOccurence}/>
		</>
	);
};

export default ListOfRisks;
